import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Confetti from '../../components/Confetti';
import LoadingOverlay from '../../components/LoadingOverlay';
import '../../tailwind.css';
import env from '../../config';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';

// URLs de Configuração (Assumindo que os arquivos de som estão na pasta 'public')
const backendUrl = env.backendUrl;
const API_LIST_URL = backendUrl + '/api/agenda/list';
const API_UPDATE_URL = backendUrl + '/api/agenda/update-status';

// Caminhos dos Sons (Assumindo que estão acessíveis via URL raiz, ex: public/assets/...)
const soundSuccess = '/assets/sound-success.wav';
const soundFail = '/assets/sound-fail.wav';

// Componente auxiliar para exibir o badge de status (inalterado)
const StatusBadge = ({ status }) => {
    let colorClasses = '';
    let text = '';

    switch (status) {
        case 'pending':
            colorClasses = 'bg-red-100 text-red-800';
            text = 'Pendente';
            break;
        case 'in_progress':
            colorClasses = 'bg-yellow-100 text-yellow-800';
            text = 'Em andamento';
            break;
        case 'completed':
            colorClasses = 'bg-green-100 text-green-800';
            text = 'Concluído';
            break;
        default:
            colorClasses = 'bg-gray-100 text-gray-800';
            text = 'Desconhecido';
    }

    return (
        <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium capitalize ${colorClasses}`}>
            {text}
        </span>
    );
};

// Formata uma data (string ou Date) para 'MM/DD' de forma compatível com iPhone 6/Safari
const formatMonthDay = (dateInput) => {
    if (!dateInput) return '-';
    // Se já for Date
    if (dateInput instanceof Date) {
        const mm = String(dateInput.getMonth() + 1).padStart(2, '0');
        const dd = String(dateInput.getDate()).padStart(2, '0');
        return `${mm}/${dd}`;
    }

    const s = String(dateInput).trim();

    // Formato comum do backend: 'YYYY-MM-DD' ou 'YYYY-MM-DD HH:MM:SS'
    let m = s.match(new RegExp('^(\\d{4})[-/](\\d{2})[-/](\\d{2})'));
    if (m) return `${m[2]}/${m[3]}`;

    // ISO com T: 'YYYY-MM-DDTHH:MM:SSZ'
    m = s.match(new RegExp('^(\\d{4})-(\\d{2})-(\\d{2})T'));
    if (m) return `${m[2]}/${m[3]}`;

    // Última tentativa: substituir espaço por 'T' e tentar criar Date
    try {
        const parsed = new Date(s.replace(' ', 'T'));
        if (!isNaN(parsed.getTime())) {
            const mm2 = String(parsed.getMonth() + 1).padStart(2, '0');
            const dd2 = String(parsed.getDate()).padStart(2, '0');
            return `${mm2}/${dd2}`;
        }
    } catch (e) {
        // ignore
    }

    // fallback: retorna original curto
    return s;
};


function Agenda() {
    const [allAgendaTasks, setAllAgendaTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeResponsavel, setActiveResponsavel] = useState(null);
    const navigate = useNavigate();
    const confettiRef = useRef(null);

    // 💥 CORREÇÃO DO HOOK: Criação das referências de áudio DENTRO do componente
    const successAudioRef = useRef(null);
    const failAudioRef = useRef(null);


    // ===============================================
    // Lógica de Áudio (Pré-carregamento)
    // ===============================================
    useEffect(() => {
        try {
            // Pré-carrega o som de sucesso
            const successAudio = new Audio(soundSuccess);
            successAudio.volume = 0.5;
            successAudioRef.current = successAudio;

            // Pré-carrega o som de falha (ou reset/reversão)
            const failAudio = new Audio(soundFail);
            failAudio.volume = 0.5;
            failAudioRef.current = failAudio;

        } catch (e) {
            // Em navegadores restritivos (como Safari mobile), pode falhar, mas o código continua
            console.warn("Não foi possível pré-carregar o áudio. O som pode ter um delay:", e);
        }
    }, []);

    // Funções de reprodução que usam as referências pré-carregadas
    const playSuccessSound = () => {
        const audio = successAudioRef.current;
        if (audio) {
            audio.currentTime = 0; // Reseta para tocar instantaneamente
            audio.play().catch(error => console.warn("Erro ao reproduzir som de sucesso:", error));
        }
    };

    const playFailSound = () => {
        const audio = failAudioRef.current;
        if (audio) {
            audio.currentTime = 0; // Reseta para tocar instantaneamente
            audio.play().catch(error => console.warn("Erro ao reproduzir som de falha:", error));
        }
    };


    // ===============================================
    // Lógica de Fetch e Persistência do Responsável
    // ===============================================
    useEffect(() => {
        const fetchTasks = async () => {
            try {
                // Fetch dos dados
                const response = await fetch(API_LIST_URL);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                const tasks = data.data;
                setAllAgendaTasks(tasks);

                // Lógica de Persistência na URL (Leitura)
                // Se houver um responsável logado no localStorage, prioriza ele
                const responsavelFromSession = localStorage.getItem('responsavel');

                if (tasks.length > 0) {
                    // Apenas usa o responsável presente em sessionStorage; se não houver, não mostra nada
                    if (responsavelFromSession && tasks.some(t => t.responsavel === responsavelFromSession)) {
                        setActiveResponsavel(responsavelFromSession);
                    } else {
                        // Não faz fallback: requer login para ver tarefas
                        setActiveResponsavel(null);
                    }
                }

            } catch (err) {
                console.error("Erro ao buscar a agenda:", err);
                setError("Não foi possível carregar a agenda. Verifique a conexão com a API.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchTasks();
    }, []);

    // Agrupamento dos dados por Responsável (inalterado)
    const groupedTasks = useMemo(() => {
        if (!allAgendaTasks.length) return {};

        return allAgendaTasks.reduce((acc, task) => {
            const responsavel = task.responsavel;
            if (!acc[responsavel]) {
                acc[responsavel] = [];
            }
            acc[responsavel].push(task);
            return acc;
        }, {});
    }, [allAgendaTasks]);

    const responsaveis = Object.keys(groupedTasks);
    // opção para mostrar/ocultar tarefas concluídas
    const [showCompleted] = useState(false);

    // opção para mostrar as tarefas de "casa" para todos os usuários
    const [showHouseTasks, setShowHouseTasks] = useState(false);
    // opção para mostrar/ocultar as atividades do usuário logado
    const [showMyTasks, setShowMyTasks] = useState(true);

    // Tasks "base" do responsável ativo (se houver)
    // const currentTasks = activeResponsavel ? allAgendaTasks.filter(t => t.responsavel === activeResponsavel) : [];

    // Lista de tarefas exibidas: combina as do responsável ativo com as de 'casa' quando a flag estiver marcada.
    const displayedTasks = useMemo(() => {
        // Começa com um array vazio e adiciona conforme as flags
        let base = [];

        // Incluir tarefas do usuário logado quando existir e for solicitado
        if (activeResponsavel && showMyTasks) {
            base = base.concat(allAgendaTasks.filter(t => t.responsavel === activeResponsavel));
        }

        // Incluir tarefas de 'casa' quando solicitado
        if (showHouseTasks) {
            const house = allAgendaTasks.filter(t => (t.responsavel || '').toLowerCase() === 'casa');
            const map = new Map();
            base.forEach(t => map.set(t.id, t));
            house.forEach(t => { if (!map.has(t.id)) map.set(t.id, t); });
            base = Array.from(map.values());
        }

        // Se nenhuma flag estiver ativada e há um responsável ativo, por compatibilidade mostramos as tarefas dele
        if (!showHouseTasks && activeResponsavel && base.length === 0 && showMyTasks) {
            base = allAgendaTasks.filter(t => t.responsavel === activeResponsavel);
        }

        // Aplica o filtro de tarefas concluídas/ignoradas quando necessário
        if (showCompleted) return base;
        return base.filter(t => t.status !== 'completed' && t.status !== 'ignored');
    }, [allAgendaTasks, activeResponsavel, showHouseTasks, showCompleted, showMyTasks]);

    // Resumo de pontos (total e concluídos) para o conjunto atualmente exibido (inclui 'casa' quando marcado)
    const pointsSummary = useMemo(() => {
        if (!displayedTasks || displayedTasks.length === 0) return { total: 0, completed: 0, percent: 0 };
        const total = displayedTasks.reduce((acc, t) => acc + (Number(t.points) || 0), 0);
        const completed = displayedTasks.filter(t => t.status === 'completed').reduce((acc, t) => acc + (Number(t.points) || 0), 0);
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
        return { total, completed, percent };
    }, [displayedTasks]);

    // const completedCount = useMemo(() => {
    //     return displayedTasks.filter(t => t.status === 'completed').length;
    // }, [displayedTasks]);

    // contador de tarefas do responsável ativo (respeita filtro de concluídas)
    const myTasksCount = useMemo(() => {
        if (!activeResponsavel) return 0;
        let tasks = allAgendaTasks.filter(t => t.responsavel === activeResponsavel);
        if (!showCompleted) tasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'ignored');
        return tasks.length;
    }, [allAgendaTasks, activeResponsavel, showCompleted]);

    // animated percent (suaviza a mudança do número)
    const [animatedPercent, setAnimatedPercent] = useState(pointsSummary.percent || 0);
    const prevPercentRef = useRef(pointsSummary.percent || 0);

    useEffect(() => {
        const start = prevPercentRef.current || 0;
        const end = pointsSummary.percent || 0;
        if (start === end) {
            setAnimatedPercent(end);
            prevPercentRef.current = end;
            return;
        }

        const duration = 600; // ms
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        const startTime = performance.now();
        let rafId = null;

        const tick = (now) => {
            const elapsed = now - startTime;
            const t = Math.min(1, elapsed / duration);
            const eased = easeOutCubic(t);
            const value = Math.round(start + (end - start) * eased);
            setAnimatedPercent(value);
            if (t < 1) {
                rafId = requestAnimationFrame(tick);
            } else {
                prevPercentRef.current = end;
            }
        };

        rafId = requestAnimationFrame(tick);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [pointsSummary.percent]);

    // número de tarefas em aberto (status != 'completed') com base no que está sendo exibido
    const openCount = useMemo(() => {
        if (!displayedTasks) return 0;
        return displayedTasks.filter(t => t.status !== 'completed').length;
    }, [displayedTasks]);

    // (removida) troca manual de responsável — agora a agenda mostra somente o responsável logado


    // ===============================================
    // Lógica de Atualização de Status
    // ===============================================
    // Logout simples: limpa localStorage e redireciona para a tela de login
    const logout = () => {
        try {
            localStorage.removeItem('user');
            localStorage.removeItem('responsavel');
            localStorage.removeItem('isAuthenticated');
        } catch (e) {
            // ignore
        }
        setActiveResponsavel(null);
        navigate('../login-images');
    };
    const handleUpdateStatus = async (task) => {
        // tenta recuperar o usuário em localStorage (salvo pelo ImageLogin)
        let sessionUser = null;
        try {
            const raw = localStorage.getItem('user');
            if (raw) sessionUser = JSON.parse(raw);
        } catch (e) {
            sessionUser = null;
        }
        let newStatus;
        if (task.status === 'pending') {
            // newStatus = 'in_progress';
            newStatus = 'completed';
        } else if (task.status === 'in_progress') {
            newStatus = 'completed';
        } else if (task.status === 'completed') {
            // Permite reverter de 'completed' para 'pending'
            newStatus = 'pending';
        } else {
            return;
        }

        setIsLoading(true);

        try {
            // Chamada à API para atualizar no Backend
            const response = await fetch(API_UPDATE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    agenda_id: task.id,
                    status: newStatus,
                    // envia quem fez a tarefa quando houver usuário em sessão
                    ...(sessionUser && sessionUser.name ? { feito_por: sessionUser.name } : {})
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Falha na API com status: ${response.status}`);
            }

            // Atualizar o estado local (UI) e registrar quem fez quando aplicável
            setAllAgendaTasks(prevTasks =>
                prevTasks.map(t => {
                    if (t.id !== task.id) return t;
                    const updated = { ...t, status: newStatus };
                    if (newStatus === 'completed') {
                        updated.feito_por = sessionUser && sessionUser.name ? sessionUser.name : t.feito_por || null;
                    } else if (newStatus === 'pending') {
                        // ao reverter, limpa o campo feito_por
                        updated.feito_por = null;
                    }
                    return updated;
                })
            );

            // Tocar o som
            if (newStatus === 'completed') {
                playSuccessSound();
                // dispara confete
                 try {
                    if (confettiRef && confettiRef.current && confettiRef.current.fire) {
                        confettiRef.current.fire(window.innerWidth/2, window.innerHeight/4, 60);
                    }
                } catch(e){}

            } else if (newStatus === 'pending') {
                playFailSound();
            } else {
                // qualquer outra mudança de status dispara confete apenas
                
            }

        } catch (err) {
            console.error("Erro ao atualizar o status:", err);
            alert(`Erro ao atualizar a tarefa: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Marca tarefa como 'ignored' no backend e atualiza o estado local
    const handleIgnoreTask = async (task) => {
        setIsLoading(true);
        try {
            // const response = await fetch(API_UPDATE_URL, {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify({ agenda_id: task.id, status: 'ignored' })
            // });

            let sessionUser = null;
            try {
                const raw = localStorage.getItem('user');
                if (raw) sessionUser = JSON.parse(raw);
            } catch (e) {
                sessionUser = null;
            }

            const response = await fetch(API_UPDATE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    agenda_id: task.id,
                    status: 'ignored',
                    // envia quem fez a tarefa quando houver usuário em sessão
                    ...(sessionUser && sessionUser.name ? { feito_por: sessionUser.name } : {})
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Erro ao ignorar tarefa');
            }

            setAllAgendaTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'ignored' } : t));
        } catch (err) {
            console.error('Erro ao ignorar tarefa', err);
            alert('Não foi possível ignorar a tarefa: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const snoozeTask = async (task) => {
        //ocultar a task temporariamente, somente no visual 
        // Obtenha uma referência para a tarefa no estado local
        const updatedTasks = allAgendaTasks.filter(t => t.id !== task.id);

        // Atualize o estado local com as tarefas atualizadas
        setAllAgendaTasks(updatedTasks);
    }

    // ... (Código de Renderização) ...

    if (isLoading && allAgendaTasks.length === 0) {
        return <div className="p-8 text-center text-blue-500 text-lg">Carregando a agenda...</div>;
    }

    if (error) {
        return <div className="p-8 text-center text-red-500 font-bold text-lg">{error}</div>;
    }

    if (responsaveis.length === 0) {
        return <div className="p-8 text-center text-gray-500 text-lg">Nenhuma tarefa encontrada na agenda.</div>;
    }

    // Se não há responsável ativo (ex.: usuário não fez login), permite ao usuário
    // marcar a opção de ver apenas as tarefas de 'casa' sem precisar logar.
    if (!activeResponsavel && !showHouseTasks) {
        return (
            <div className="p-8 text-center">
                <p className="text-lg text-gray-700">Nenhum responsável logado.</p>
                <p className="text-sm text-gray-500">Por favor, faça login para ver suas tarefas ou marque abaixo para ver apenas as tarefas de casa.</p>
                <div className="mt-4 flex items-center justify-center gap-4">
                    <label className="flex items-center text-sm text-gray-600">
                        <input type="checkbox" checked={showHouseTasks} onChange={(e) => setShowHouseTasks(e.target.checked)} className="mr-2" /> Mostrar tarefas de casa para todos
                    </label>
                    <a href="../apps/login-images" className="default-btn">Ir para login</a>
                </div>
            </div>
        );
    }

    // 4. Componente Principal com Tabela Responsiva
    return (
        <div className="p-4 sm:p-8">
            <LoadingOverlay visible={isLoading} message={isLoading ? 'Aguarde, atualizando...' : 'Processando...'} />
            <Confetti ref={confettiRef} />
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-3xl font-bold text-gray-800">Agenda de Tarefas</h1>
                    {/* badge desktop */}
                    <div className="hidden sm:flex items-center bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3" /></svg>
                        Abertas: {openCount}
                    </div>
                </div>
                <div>
                    {activeResponsavel ? (
                        <button onClick={logout} className="default-btn" style={{background:'#ef4444', color:'#fff'}}>Sair</button>
                    ) : (
                        <a href="/login-images" className="default-btn">Entrar</a>
                    )}
                </div>
            </div>

            {/* Mostra apenas as tarefas do responsável logado */}

            {/* CONTEÚDO DA AGENDA (FILTRADO) */}
            <div className="mt-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="text-xl font-semibold text-gray-700">
                        {activeResponsavel}
                    </h2>
                    <div className="flex flex-wrap items-center gap-4">
                        {showCompleted && (
                          <div className="hidden sm:block text-sm text-gray-600">Concluídas: <strong>{pointsSummary.completed}</strong> / Total: <strong>{pointsSummary.total}</strong> pontos <span className="text-gray-500">({animatedPercent}%)</span></div>
                        )}
                        <label className="flex items-center text-sm text-gray-600">
                            <input type="checkbox" checked={showMyTasks} onChange={(e) => setShowMyTasks(e.target.checked)} className="mr-2" /> Minhas {`(${myTasksCount})`}
                        </label>
                        <label className="flex items-center text-sm text-gray-600">
                            <input type="checkbox" checked={showHouseTasks} onChange={(e) => setShowHouseTasks(e.target.checked)} className="mr-2" /> Casa
                        </label>
                    </div>
                </div>

                    {/* versão mobile: destaque em bloco grande */}
                                        {showCompleted && (
                                            <div className="sm:hidden mb-4 p-3 bg-indigo-50 rounded-lg text-center">
                                                    <div className="text-base font-semibold text-indigo-700">Pontos</div>
                                                    <div className="text-lg font-bold text-indigo-900">{pointsSummary.completed} <span className="text-sm font-normal text-indigo-700">/ {pointsSummary.total}</span> <span className="text-base font-semibold text-indigo-700">({animatedPercent}%)</span></div>
                                                            <div className="text-sm text-indigo-600">concluídos / total</div>
                                            </div>
                                        )}

                        {/* Mobile: toggle para mostrar tarefas de casa */}
                        <div className="sm:hidden space-y-4">
                            {displayedTasks.map((task) => {
                                const isHouse = ((task.responsavel || '').toString().toLowerCase() === 'casa');
                                return (
                                    <div key={task.id} className={`p-4 rounded-lg shadow-sm hover:shadow-md transition duration-150 border ${isHouse ? 'bg-yellow-50 border-yellow-300' : 'bg-white border-gray-200'}`}>

                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-3">
                                                <span className="text-base font-bold text-gray-900">{task.label}</span>
                                                {isHouse && (
                                                    <span title="Tarefa de casa" className="inline-flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9.75L12 3l9 6.75V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.75z" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                            <StatusBadge status={task.status} />
                                        </div>

                                            {task.feito_por && (
                                                <div className="text-sm text-gray-500 mb-2">Feito por: <span className="font-semibold text-gray-700">{task.feito_por}</span></div>
                                            )}

                                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                            <div className="text-sm text-gray-500 font-medium">
                                                Frequência: <span className="font-semibold text-gray-700">{task.frequency}d</span>
                                            </div>
                                            <div className="text-sm text-gray-500 font-medium">
                                                Pontos: <span className="font-semibold text-gray-700">{task.points}</span>
                                            </div>

                                            
                                        </div>
                                        <div className="text-sm text-gray-500 font-medium mt-3">
                                            <span className="font-semibold text-gray-700">
                                                {formatMonthDay(task.created_at)}
                                            </span>
                                        </div>
                                        <div className="task-actions mt-4">
                                            <button
                                                onClick={() => handleUpdateStatus(task)}
                                                disabled={isLoading}
                                                className={`text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800
                                                    ${task.status === 'completed'
                                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                    }`}
                                            >
                                                {/* TEXTO DO BOTÃO DINÂMICO */}
                                                {/* {task.status === 'pending' ? 'C' :
                                                    task.status === 'in_progress' ? 'C' : 'R'} */}
                                                <DoneIcon />
                                            </button>
                                            <button onClick={() => handleIgnoreTask(task)} 
                                                    type="button" 
                                                    className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800">
                                                    <DeleteIcon />
                                            </button>
                                            <button
                                                onClick={() => snoozeTask(task)}
                                                disabled={isLoading}
                                                className="text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                                            >
                                                <NotificationsPausedIcon />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>


                {/* TABELA TRADICIONAL PARA DESKTOP */}
                <div className="hidden sm:block overflow-x-auto shadow-lg rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr className="bg-gray-100 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Atividade</th>
                                <th className="px-6 py-3">Feito por</th>
                                <th className="px-6 py-3 text-center">Freq.</th>
                                <th className="px-6 py-3 text-center">Status</th>
                                <th className="px-6 py-3 text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedTasks.map((task) => {
                                const isHouse = ((task.responsavel || '').toString().toLowerCase() === 'casa');
                                return (
                                    <tr key={task.id} className={`border-b transition duration-150 ${isHouse ? 'bg-yellow-50 border-yellow-300' : 'border-gray-100 hover:bg-indigo-50/50'}`}>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                            {formatMonthDay(task.created_at)}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900 flex items-center gap-2">
                                            <span>{task.label}</span>
                                            {isHouse && (
                                                <span title="Tarefa de casa" className="inline-flex items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9.75L12 3l9 6.75V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.75z" />
                                                    </svg>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{task.feito_por ? (<span className="font-medium text-gray-700">{task.feito_por}</span>) : (<span className="text-sm text-gray-400">-</span>)}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500 text-center">{task.frequency}d</td>
                                        <td className="px-6 py-4 text-center">
                                            <StatusBadge status={task.status} />
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center">
                                                <button
                                                    onClick={() => handleUpdateStatus(task)}
                                                    disabled={isLoading}
                                                    className={`px-4 py-2 text-sm font-semibold rounded-full transition duration-150 ${task.status === 'completed' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                                                    {task.status === 'pending' ? 'CONCLUIR' : task.status === 'in_progress' ? 'CONCLUIR' : 'REVERTER'}
                                                </button>
                                                <button
                                                    onClick={() => handleIgnoreTask(task)}
                                                    disabled={isLoading}
                                                    className="ml-2 px-3 py-1 text-sm font-semibold rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300"
                                                >
                                                    Ignorar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Agenda;